import { useEffect, useState } from "react";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Container } from "react-bootstrap";
import {
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { type FAMI, FAMI_TEMPLATE } from "@/models/fami.tsx";
import { useCoreData } from "@/queries.ts";
import { FaUsers, FaHome } from "react-icons/fa";

import { useFAMIFormLogic } from "./useFAMIFormLogic";
import DateInput from "@/components/shared/forms/DateInput";
import { getStateOptionsCaliforniaFirst } from "@/utils/states";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";

type ServiceReasonOption = {
  Text: string;
  Value: string;
};

type ServiceReasonsMultiSelectProps = {
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  disabled: boolean;
  options: ServiceReasonOption[];
  initialValues: string[];
  onChangeValues: (values: string[]) => void;
  maxSelections?: number;
  placeholder?: string;
  resetKey: string;
  errorMessage?: string;
  afterChange?: () => void;
  isRequired?: boolean;
};

function ServiceReasonsMultiSelect({
  id,
  ariaLabel,
  ariaDescribedBy,
  disabled,
  options,
  initialValues,
  onChangeValues,
  maxSelections = 9,
  placeholder = "Select up to nine reasons",
  resetKey,
  errorMessage,
  afterChange,
  isRequired,
}: ServiceReasonsMultiSelectProps) {
  const [selected, setSelected] = useState<ServiceReasonOption[]>(() => {
    const initialSet = initialValues.filter(Boolean);
    return initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is ServiceReasonOption => !!o)
      .slice(0, maxSelections);
  });

  useEffect(() => {
    const initialSet = initialValues.filter(Boolean);
    const mapped = initialSet
      .map((val) => options.find((o) => o.Value === val))
      .filter((o): o is ServiceReasonOption => !!o)
      .slice(0, maxSelections);

    setSelected(mapped);
  }, [resetKey, initialValues, options, maxSelections]);

  const handleChange = (e: MultiSelectChangeEvent) => {
    const next = (e.value as ServiceReasonOption[]) ?? [];
    const limited = next.slice(0, maxSelections);

    setSelected(limited);
    onChangeValues(limited.map((o) => o.Value));
    afterChange?.();
  };

  const effectiveAriaLabel =
    isRequired && ariaLabel ? `${ariaLabel} (required)` : ariaLabel;

  useMultiSelectChipListLabel(id, effectiveAriaLabel, selected);

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // option list without having to type first.
  const [opened, setOpened] = useState(false);

  useMultiSelectListboxMultiselectable(id, opened);

  return (
    <MultiSelect
      id={id}
      ariaLabel={effectiveAriaLabel}
      ariaDescribedBy={ariaDescribedBy}
      data={options}
      textField="Text"
      dataItemKey="Value"
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder={placeholder}
      valid={!errorMessage}
      validationMessage={errorMessage}
      inputAttributes={errorMessage ? { "aria-invalid": true } : undefined}
      required={isRequired}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      itemRender={multiSelectSelectedItemRender}
      popupSettings={id ? { className: `${id}-listbox-popup` } : undefined}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {selected.length > 0 && (
            <MultiSelectClearButton
              onClear={() =>
                handleChange({ value: [] } as MultiSelectChangeEvent)
              }
              disabled={disabled}
            />
          )}
        </>
      }
    />
  );
}

const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

const ADDRESS_CHARS_REGEX = /^[A-Za-z0-9À-ÖØ-öø-ÿ.\-'/ ]*$/;
const ADDRESS_CHARS_MESSAGE =
  "Only letters, numbers, periods, hyphens, apostrophes, forward slashes, and spaces are allowed";

function validateAddressChars(value?: string) {
  return !value || ADDRESS_CHARS_REGEX.test(value) ? true : ADDRESS_CHARS_MESSAGE;
}

export function FAMIAddEditForm({
  selectedFamily,
  form,
  familyDefaults,
}: {
  selectedFamily: FAMI | undefined;
  form: UseFormReturn<FAMI, any, FAMI>;
  familyDefaults?: Partial<FAMI>;
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/child/$entityid",
  });

  const csppid = params?.entityid ?? "";

  const newFamilyTemplate: FAMI = {
    ...FAMI_TEMPLATE,
    ...familyDefaults,
    CSPPID: csppid,
  };

  const rawFamily = selectedFamily ?? newFamilyTemplate;
  const family = {
    ...rawFamily,
    EffectiveStartDate: rawFamily.EffectiveStartDate
      ? new Date(rawFamily.EffectiveStartDate).toISOString().split("T")[0]
      : "",
  };

  const leaContext = useRouteContext({ from: "/$leaid" });
  const viewOnly = !leaContext.hasRole("FAMIEdit");
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("FAMI");

  const {
    register,
    control,
    formState: { errors },
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });

  const resetKey = selectedFamily?.LocalFamilyId || `new:${csppid}`;
  const isEdit = Boolean(selectedFamily?.LocalFamilyId);

  const {
    showMailingAddress,
    handleMailingToggle,
    isSecondHohRequired,
    requireResidentialAddress1,
    requireResidentialCityStateZip,
    requireMailingCityStateZip,
    isExperiencingHomelessness,
    getServiceReasons,
    setServiceReasons,
    hasAnyServiceReasonSelected,
    validateEffectiveStartDate,
    validateCalWorksRecipient,
  } = useFAMIFormLogic({
    form,
    family,
    resetKey,
    isEdit,
    reasonForServiceList: coreData.reasonForServiceList,
    calWORKsList: coreData.calWORKsList,
  });

  const stateOptions = getStateOptionsCaliforniaFirst(coreData.states);

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaUsers className="section-icon" />
          <h5 className="section-title">Family Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(name, {
                    validate: validateEffectiveStartDate,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="LocalFamilyId"
              labelText="Family Local ID (FICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!family.LocalFamilyId}
                  maxLength={30}
                  {...register(name, {
                    required: "Family Local ID (FICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!family.CSPPID}
                  maxLength={50}
                  {...register(name, { required: "CSPPID is required" })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly={!!family.LocalChildId}
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedFamily?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, { required: "Preschool is required" })}
                  options={leaContext
                    .currentOrgPreschoolsByProgramYear(programYear)
                    .map((x) => ({
                      Text: x.PreschoolName,
                      Value: x.PreschoolCDSCode,
                    }))}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="FamilySize"
              labelText="Family Size"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                    if (
                      !/^\d$/.test(e.key) &&
                      ![
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                  {...register(name, {
                    required: "Family Size is required",
                    validate: (value) => {
                      const n = Number(value);
                      if (!Number.isInteger(n))
                        return "Family Size must be a whole number";
                      if (n < 1) return "Family Size must be at least 1";

                      const formValues = form.getValues();
                      const hasHoh2 =
                        formValues.HeadOfHousehold2FirstName?.trim() ||
                        formValues.HeadOfHousehold2LastName?.trim();

                      if (hasHoh2 && n < 3) {
                        return "Family Size must be at least 3 when a second head of household is entered";
                      }

                      return true;
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="FamilyIncome"
              labelText="Family Monthly Income"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                    if (
                      !/^\d$/.test(e.key) &&
                      ![
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text");
                    if (!/^\d+$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                  {...register(name, {
                    required: "Income is required",
                    validate: (value) => {
                      const n = Number(value);
                      if (!Number.isInteger(n))
                        return "Income must be a whole number";
                      if (n < 0 || n > 99999)
                        return "Income must be between 0 and 99999";
                      return true;
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="CALWorksRecipient"
              labelText="CalWORKs Recipient"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "CalWORKs recipient is required",
                    validate: validateCalWorksRecipient,
                  })}
                  options={coreData.calWORKsList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />
        <br />

        <h6>1st Head of Household</h6>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="HeadOfHousehold1FirstName"
              labelText="First Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name, { required: "First Name is required" })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="HeadOfHousehold1MiddleName"
              labelText="Middle Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="HeadOfHousehold1LastName"
              labelText="Last Name"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={50}
                  {...register(name, { required: "Last Name is required" })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="HeadOfHousehold1NeedForService1"
              labelText="Reasons For Needing Service"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <div>
                  {/* Hidden fields keep NeedForService1..9 in form state */}
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService1", {
                      validate: () =>
                        hasAnyServiceReasonSelected("HeadOfHousehold1")
                          ? true
                          : "Reasons for needing service is required",
                    })}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService2")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService3")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService4")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService5")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService6")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService7")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService8")}
                  />
                  <input
                    type="hidden"
                    {...register("HeadOfHousehold1NeedForService9")}
                  />
                  <ServiceReasonsMultiSelect
                    id={id}
                    ariaLabel="Reasons For Needing Service"
                    ariaDescribedBy={validationProps["aria-describedby"]}
                    key={`${resetKey}:hoh1`}
                    disabled={viewOnly}
                    resetKey={resetKey}
                    options={coreData.reasonForServiceList}
                    initialValues={getServiceReasons("HeadOfHousehold1")}
                    maxSelections={9}
                    placeholder="Select up to nine reasons"
                    isRequired
                    onChangeValues={(values) =>
                      setServiceReasons("HeadOfHousehold1", values)
                    }
                    errorMessage={
                      errors.HeadOfHousehold1NeedForService1?.message as
                        | string
                        | undefined
                    }
                    afterChange={() =>
                      form.trigger("HeadOfHousehold1NeedForService1")
                    }
                  />
                </div>
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="HeadOfHousehold1HighestEducationLevel"
              labelText="Highest Education Level"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Highest Education Level is required",
                  })}
                  options={coreData.educationList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />

        <>
          <div className="d-flex align-items-center justify-content-between">
            <h6 className="mb-0">2nd Head of Household</h6>
          </div>

          <div className="mt-3 row g-3">
            <div className="col-md-4">
              <FormFieldRow
                name="HeadOfHousehold2FirstName"
                labelText="First Name"
                errors={errors}
                tooltipMap={tooltipMap}
                labelCol={12}
                inputCol={12}
                isRequired={isSecondHohRequired}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    maxLength={30}
                    {...register(name, {
                      validate: (value) => {
                        if (isSecondHohRequired && !value?.trim()) {
                          return "First Name is required";
                        }
                        return true;
                      },
                    })}
                  />
                )}
              </FormFieldRow>
            </div>

            <div className="col-md-4">
              <FormFieldRow
                name="HeadOfHousehold2MiddleName"
                labelText="Middle Name"
                errors={errors}
                tooltipMap={tooltipMap}
                labelCol={12}
                inputCol={12}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    maxLength={30}
                    {...register(name)}
                  />
                )}
              </FormFieldRow>
            </div>

            <div className="col-md-4">
              <FormFieldRow
                name="HeadOfHousehold2LastName"
                labelText="Last Name"
                errors={errors}
                tooltipMap={tooltipMap}
                labelCol={12}
                inputCol={12}
                isRequired={isSecondHohRequired}
              >
                {(id, name) => (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    maxLength={50}
                    {...register(name, {
                      validate: (value) => {
                        if (isSecondHohRequired && !value?.trim()) {
                          return "Last Name is required";
                        }
                        return true;
                      },
                    })}
                  />
                )}
              </FormFieldRow>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <FormFieldRow
                name="HeadOfHousehold2NeedForService1"
                labelText="Reasons For Needing Service"
                errors={errors}
                tooltipMap={tooltipMap}
                labelCol={12}
                inputCol={12}
                isRequired={isSecondHohRequired}
              >
                {(id, name, validationProps) => (
                  <div>
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService1", {
                        validate: () => {
                          if (!isSecondHohRequired) return true;
                          return hasAnyServiceReasonSelected("HeadOfHousehold2")
                            ? true
                            : "Reasons For Needing Service is required";
                        },
                      })}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService2")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService3")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService4")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService5")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService6")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService7")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService8")}
                    />
                    <input
                      type="hidden"
                      {...register("HeadOfHousehold2NeedForService9")}
                    />

                    <ServiceReasonsMultiSelect
                      id={id}
                      ariaLabel="Reasons For Needing Service"
                      ariaDescribedBy={validationProps["aria-describedby"]}
                      key={`${resetKey}:hoh2`}
                      disabled={viewOnly}
                      resetKey={resetKey}
                      options={coreData.reasonForServiceList}
                      initialValues={getServiceReasons("HeadOfHousehold2")}
                      maxSelections={9}
                      placeholder="Select up to nine reasons"
                      isRequired={isSecondHohRequired}
                      onChangeValues={(values) =>
                        setServiceReasons("HeadOfHousehold2", values)
                      }
                      errorMessage={
                        errors.HeadOfHousehold2NeedForService1?.message as
                          | string
                          | undefined
                      }
                      afterChange={() =>
                        form.trigger("HeadOfHousehold2NeedForService1")
                      }
                    />
                  </div>
                )}
              </FormFieldRow>
            </div>

            <div className="col-md-6">
              <FormFieldRow
                name="HeadOfHousehold2HighestEducationLevel"
                labelText="Highest Education Level"
                errors={errors}
                tooltipMap={tooltipMap}
                labelCol={12}
                inputCol={12}
                isRequired={isSecondHohRequired}
              >
                {(id, name) => (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      validate: (value) => {
                        if (isSecondHohRequired && !value) {
                          return "Highest Education Level is required";
                        }
                        return true;
                      },
                    })}
                    options={coreData.educationList}
                  />
                )}
              </FormFieldRow>
            </div>
          </div>
        </>

        <br />

        <h6>Communication Preference</h6>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="HeadOfHouseholdWrittenCommunicationPreference"
              labelText="Written"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name)}
                  options={coreData.languagesList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="HeadOfHouseholdVerbalCommunicationPreference"
              labelText="Verbal"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name)}
                  options={coreData.languagesList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaHome className="section-icon" />
          <h5 className="section-title">Address Information</h5>
        </div>

        <br />
        <h6>Home Address</h6>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="ResidentialAddress1"
              labelText="Address Line 1"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={requireResidentialAddress1}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={60}
                  {...register(name, {
                    validate: (value) => {
                      if (!isExperiencingHomelessness && !value?.trim()) {
                        return "Address Line 1 is required";
                      }
                      return validateAddressChars(value);
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="ResidentialAddress2"
              labelText="Address Line 2"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={60}
                  {...register(name, { validate: validateAddressChars })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ResidentialCity"
              labelText="City"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={requireResidentialCityStateZip}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={30}
                  {...register(name, {
                    validate: (value) => {
                      if (requireResidentialCityStateZip && !value?.trim()) {
                        return "City is required";
                      }
                      return validateAddressChars(value);
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ResidentialState"
              labelText="State"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={requireResidentialCityStateZip}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    validate: (value) => {
                      if (requireResidentialCityStateZip && !value) {
                        return "State is required";
                      }
                      return true;
                    },
                  })}
                  options={stateOptions}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ResidentialZip"
              labelText="Zip"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={requireResidentialCityStateZip}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  maxLength={10}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                    if (
                      !/[\d-]/.test(e.key) &&
                      ![
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("text").trim();
                    if (!/^\d{5}(-\d{4})?$/.test(pasted)) {
                      e.preventDefault();
                    }
                  }}
                  {...register(name, {
                    validate: (value) => {
                      if (!value?.trim()) {
                        return requireResidentialCityStateZip
                          ? "Zip is required"
                          : true;
                      }
                      return ZIP_REGEX.test(value)
                        ? true
                        : "Zip must be in the format 99999 or 99999-9999";
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <br />
        <br />

        <div
          className="d-flex align-items-center mb-3 p-3 rounded"
          style={{
            backgroundColor: "#eef4ff",
            border: "1px solid #cfe2ff",
            borderLeft: "5px solid #0d6efd",
          }}
        >
          <input
            type="checkbox"
            id="mailingDifferent"
            className="form-check-input me-2"
            checked={showMailingAddress}
            onChange={(e) => handleMailingToggle(e.target.checked)}
            disabled={viewOnly}
          />
          <label
            htmlFor="mailingDifferent"
            className="form-check-label fw-semibold"
          >
            Mailing address is different from home address
          </label>
        </div>

        {showMailingAddress && (
          <>
            <h6>Mailing Address</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <FormFieldRow
                  name="MailingAddress1"
                  labelText="Address Line 1"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={viewOnly}
                      maxLength={60}
                      {...register(name, {
                        validate: (value) => {
                          if (!value?.trim()) {
                            return "Address Line 1 is required";
                          }
                          return validateAddressChars(value);
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-6">
                <FormFieldRow
                  name="MailingAddress2"
                  labelText="Address Line 2"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={viewOnly}
                      maxLength={60}
                      {...register(name, { validate: validateAddressChars })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <FormFieldRow
                  name="MailingCity"
                  labelText="City"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired={requireMailingCityStateZip}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={viewOnly}
                      maxLength={30}
                      {...register(name, {
                        validate: (value) => {
                          if (requireMailingCityStateZip && !value?.trim()) {
                            return "City is required";
                          }
                          return validateAddressChars(value);
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="MailingState"
                  labelText="State"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired={requireMailingCityStateZip}
                >
                  {(id, name) => (
                    <Select
                      id={id}
                      disabled={viewOnly}
                      {...register(name, {
                        validate: (value) => {
                          if (requireMailingCityStateZip && !value) {
                            return "State is required";
                          }
                          return true;
                        },
                      })}
                      options={stateOptions}
                    />
                  )}
                </FormFieldRow>
              </div>

              <div className="col-md-4">
                <FormFieldRow
                  name="MailingZip"
                  labelText="Zip"
                  errors={errors}
                  tooltipMap={tooltipMap}
                  labelCol={12}
                  inputCol={12}
                  isRequired={requireMailingCityStateZip}
                >
                  {(id, name) => (
                    <TextInput
                      id={id}
                      disabled={viewOnly}
                      maxLength={10}
                      inputMode="numeric"
                      onKeyDown={(e) => {
                        if (
                          !/[\d-]/.test(e.key) &&
                          ![
                            "Backspace",
                            "Delete",
                            "ArrowLeft",
                            "ArrowRight",
                            "Tab",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData("text").trim();
                        if (!/^\d{5}(-\d{4})?$/.test(pasted)) {
                          e.preventDefault();
                        }
                      }}
                      {...register(name, {
                        validate: (value) => {
                          if (!value?.trim()) {
                            return requireMailingCityStateZip
                              ? "Zip is required"
                              : true;
                          }
                          return ZIP_REGEX.test(value)
                            ? true
                            : "Zip must be in the format 99999 or 99999-9999";
                        },
                      })}
                    />
                  )}
                </FormFieldRow>
              </div>
            </div>
          </>
        )}
      </div>

      <span
        id="spnAddOrEditClassroomErrorOverall"
        className="row ps-3 mt-3 pe-3 float-end"
        style={{ color: "#CC0000" }}
      ></span>
    </Container>
  );
}
